import React, { useState, useRef, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { TooltipProps } from '../utils'

const Tooltip: React.FC<TooltipProps> = ({
  content,
  position = 'top',
  children,
  className = 'w-fit',
  textAlign = 'text-center',
  cssClassName = '',
}) => {
  const [isHovered, setIsHovered] = useState<boolean>(false)
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number
    left: number
  }>({ top: 0, left: 0 })
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const childRef = useRef<HTMLDivElement | null>(null)

  const handleScroll = () => {
    if (isHovered) {
      setIsHovered(false)
    }
  }

  useEffect(() => {
    if (isHovered && childRef.current && tooltipRef.current) {
      const childRect = childRef.current.getBoundingClientRect()
      const tooltipRect = tooltipRef.current.getBoundingClientRect()

      const positions = {
        top: {
          top: childRect.top - tooltipRect.height - 8,
          left: childRect.left,
        },
        bottom: {
          top: childRect.bottom + 8,
          left: childRect.left + childRect.width / 2 - tooltipRect.width / 2,
        },
        left: {
          top: childRect.top + childRect.height / 2 - tooltipRect.height / 2,
          left: childRect.left - tooltipRect.width - 8,
        },
        right: {
          top: childRect.top + childRect.height / 2 - tooltipRect.height / 2,
          left: childRect.right + 8,
        },
      }

      const calculatedPosition = positions[position]

      if (calculatedPosition.left < 0) {
        calculatedPosition.left = 8
      } else if (
        calculatedPosition.left + tooltipRect.width >
        window.innerWidth
      ) {
        calculatedPosition.left = window.innerWidth - tooltipRect.width - 8
      }

      if (calculatedPosition.top < 0) {
        calculatedPosition.top = childRect.bottom + 8
      } else if (
        calculatedPosition.top + tooltipRect.height >
        window.innerHeight
      ) {
        calculatedPosition.top = childRect.top - tooltipRect.height - 8
      }

      setTooltipPosition(calculatedPosition)
    }
  }, [isHovered, position])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true)
    return () => {
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [isHovered])

  return content ? (
    <div
      className={`block ${cssClassName}`}
      ref={childRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => setIsHovered(false)}
    >
      {children}
      {isHovered &&
        ReactDOM.createPortal(
          <div
            ref={tooltipRef}
            className={`font-primary font-light max-w-[17rem] text-wrap break-words ${textAlign} absolute px-3 py-1 bg-accent-900 text-accent-0 z-[99] text-xs rounded shadow-lg pointer-events-none ${className}`}
            style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
          >
            {typeof content === 'string' &&
            /<\/?[a-z][\s\S]*>/i.test(content) ? (
              <div dangerouslySetInnerHTML={{ __html: content }} />
            ) : (
              content
            )}
          </div>,
          document.body
        )}
    </div>
  ) : (
    <div className={`block ${cssClassName}`}>{children}</div>
  )
}

export default Tooltip
